import { formatPublicSourceLabel } from "@/features/machines/addressing";
import type { DashboardWidget, ResolvedWidgetValue } from "../../types";
import { resolveStopAlarm, useStopAlarm } from "../../useStopAlarm";
import { formatWidgetRelative } from "./format";
import { HeroCard, HeroMeaning, type HeroTone } from "./HeroCard";

interface ProductionStatusWidgetProps {
  widget: DashboardWidget;
  value: ResolvedWidgetValue;
  editable?: boolean;
  onConfigure?: () => void;
}

/**
 * Estado de produção no cartão herói: a palavra do estado (Produzindo / Parado
 * / Em movimento / Sem movimento) é o herói com um LED, e o detalhe vira a
 * linha de significado em sentence case. Saiu o rótulo "PRODUZINDO" em caixa
 * alta do shell legado.
 */
export function ProductionStatusWidget({
  widget,
  value,
  editable,
  onConfigure,
}: ProductionStatusWidgetProps) {
  const isProducing = resolveProductionNow(widget, value);
  const describesMovement = widget.settings.availability?.mode === "activity";
  const isWaitingAccumulator = value.accumulatorDelta?.status === "warming";
  const label =
    isProducing === null
      ? isWaitingAccumulator
        ? "Aguardando ciclo"
        : "Sem sinal"
      : isProducing
        ? describesMovement
          ? "Em movimento"
          : "Produzindo"
        : describesMovement
          ? "Sem movimento"
          : "Parado";
  const meta =
    isProducing === null
      ? isWaitingAccumulator
        ? "Aguardando próximo acumulado"
        : "Aguardando telemetria"
      : isProducing
        ? value.accumulatorDelta
          ? "Acumulado avançou no ciclo"
          : "Atividade detectada"
        : value.accumulatorDelta
          ? "Acumulado sem avanço"
          : "Sem atividade";

  // Alarme de parada: parada (não-produzindo) + fresca além da tolerância.
  const stop = resolveStopAlarm(widget);
  const alarming = useStopAlarm({
    enabled: stop.enabled,
    stopped: isProducing === false,
    fresh: isProducing !== null,
    afterMinutes: stop.afterMinutes,
  });
  const meaningTone: HeroTone = alarming
    ? "danger"
    : isProducing === null
      ? "muted"
      : isProducing
        ? "ok"
        : "warn";
  const cardTone = alarming ? "danger" : isProducing === false ? "warn" : "ok";

  return (
    <HeroCard
      title={widget.title}
      caption={formatPublicSourceLabel(widget.source, widget.settings.signalKind)}
      tone={cardTone}
      alarm={alarming || widget.settings.alarmEnabled}
      editable={editable}
      onConfigure={onConfigure}
      footnote={value.measuredAt ? `Atualizado ${formatWidgetRelative(value.measuredAt) ?? "agora"}` : null}
    >
      <div className="w-hero__state">
        <span className={isProducing ? "led on" : "led"} aria-hidden="true" />
        <span className="w-hero__state-label">{label}</span>
      </div>

      <HeroMeaning tone={meaningTone} animated={isProducing === true}>
        {alarming ? "Parada além do tolerado" : meta}
      </HeroMeaning>
    </HeroCard>
  );
}

function resolveProductionNow(
  widget: DashboardWidget,
  value: ResolvedWidgetValue,
): boolean | null {
  if (!value.isAvailable || value.numericValue === null) return null;
  const config = widget.settings.availability;

  if (config?.mode === "state") {
    const activeWhen = config.activeWhen ?? 1;
    return value.numericValue === activeWhen;
  }

  const threshold = config?.minActiveValue ?? 0;
  return Math.abs(value.numericValue) > threshold;
}
